import { useEffect, useState, useContext } from "react";
import { getUserAddresses, deleteUserAddress } from "../../../users/functions";
import Loading from "../../../atoms/loading";
import Button from "../../../atoms/button";
import NewAddress from "../../../users/user/newAddress";
import ToastContext from "../../../../context/toastProvider";

const DELIVERY_OPTIONS = [
    { value: "correios", label: "Correios", hint: "Envio para o endereço do cliente" },
    { value: "presencial", label: "Presencial", hint: "Retirada em loja parceira" },
];

const DeliveryStep = ({ user, userData, setStep, setDeliveryData, stores, deliveryData }) => {

    const { goodToast, badToast } = useContext(ToastContext);

    const [delivery, setDelivery] = useState(deliveryData?.delivery || "");
    const [userAddresses, setUserAddresses] = useState([]);
    const [address, setAddress] = useState(deliveryData?.address || null);
    const [store, setStore] = useState(deliveryData?.store || "");
    const [newAddress, setNewAddress] = useState(false);

    const [loading, setLoading] = useState(false);

    // id do endereço com confirmação de exclusão aberta (null = nenhum).
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    useEffect(() => {
        if (delivery === "correios") {
            doGetUserAddresses();
        }
        // eslint-disable-next-line
    }, [delivery]);

    const doGetUserAddresses = async () => {
        setLoading(true);
        const data = await getUserAddresses(user?.token, userData?._id);
        if (data?.type === "success") {
            setUserAddresses(data?.data);
        }
        setLoading(false);
    }

    const doDeleteUserAddress = async (e, id) => {
        e.preventDefault();
        e.stopPropagation();
        setLoading(true);
        const data = await deleteUserAddress(user?.token, id);
        if (data?.type !== "success") {
            setLoading(false);
            badToast(data?.message);
            return null;
        }
        setLoading(false);
        setAddress(null);
        setConfirmDeleteId(null);
        goodToast("Endereço excluído!");
        doGetUserAddresses();
    }

    const handleConfirmDelete = (e, id) => {
        e.stopPropagation();
        setConfirmDeleteId(id);
    }

    const handleCancelDelete = (e) => {
        e.stopPropagation();
        setConfirmDeleteId(null);
    }

    const handleNext = () => {
        if (disableNext) {
            return null;
        }
        if (delivery === "correios") {
            setDeliveryData({ delivery, address });
        }
        if (delivery === "presencial") {
            setDeliveryData({ delivery, store });
        }
        setStep(2);
    }

    const goBack = () => {
        setStep(0);
    }

    const refreshAddresses = () => {
        setNewAddress(false);
        setDelivery("correios");
        setAddress(null);
        doGetUserAddresses();
    }

    const disableNext = delivery === "" || (delivery === "correios" && !address) || (delivery === "presencial" && !store);

    if (newAddress) {
        return (
            <NewAddress user={user} userId={userData?._id} refresh={refreshAddresses} cancel={() => setNewAddress(false)} />
        )
    }

    return (
        <div className="relative">
            <p className="mb-3 text-sm text-laranjinha/60">Como o cliente vai receber o pedido?</p>
            <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Tipo de entrega">
                {DELIVERY_OPTIONS.map((option) => {
                    const selected = delivery === option.value;
                    return (
                        <button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => setDelivery(option.value)}
                            className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition ${
                                selected ? "border-primary bg-mgs-elevated" : "border-primary/25 bg-mgs-surface/60 hover:border-primary/60"
                            }`}
                        >
                            <span
                                aria-hidden="true"
                                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${selected ? "border-primary" : "border-primary/40"}`}
                            >
                                {selected ? <span className="h-2 w-2 rounded-full bg-primary"></span> : null}
                            </span>
                            <span>
                                <span className="block font-medium text-laranjinha">{option.label}</span>
                                <span className="block text-xs text-laranjinha/50">{option.hint}</span>
                            </span>
                        </button>
                    );
                })}
            </div>

            {
                delivery === "correios" ? (
                    <>
                        <div className="mb-2 flex items-center justify-between">
                            <p className="text-sm text-laranjinha/60">
                                {userAddresses?.length
                                    ? "Selecione o endereço de entrega:"
                                    : "O cliente ainda não tem endereço cadastrado."}
                            </p>
                            <button
                                type="button"
                                onClick={() => setNewAddress(true)}
                                className="whitespace-nowrap rounded-lg border border-primary/40 bg-mgs-surface px-3 py-1 text-sm text-laranjinha hover:border-primary"
                            >
                                + Cadastrar endereço
                            </button>
                        </div>
                        <div role="radiogroup" aria-label="Endereços do cliente" className="flex flex-col gap-2">
                            {
                                userAddresses?.map((a, i) => {
                                    const selected = address === a;
                                    const confirming = confirmDeleteId === a?._id;
                                    return (
                                        <div
                                            key={a?._id ?? i}
                                            role="radio"
                                            aria-checked={selected}
                                            tabIndex={0}
                                            onClick={() => setAddress(a)}
                                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setAddress(a); }}
                                            className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition ${
                                                selected ? "border-primary bg-mgs-elevated" : "border-primary/25 bg-mgs-surface/60 hover:border-primary/60"
                                            }`}
                                        >
                                            <span
                                                aria-hidden="true"
                                                className={`mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${selected ? "border-primary" : "border-primary/40"}`}
                                            >
                                                {selected ? <span className="h-2 w-2 rounded-full bg-primary"></span> : null}
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block font-medium text-laranjinha">{a?.name}</span>
                                                <span className="block text-sm text-laranjinha/70">
                                                    {a?.street}, {a?.houseNumber} — {a?.city}/{a?.state}
                                                </span>
                                                <span className="block text-xs text-laranjinha/50">
                                                    CEP {a?.postalCode} · {a?.district}
                                                    {a?.complement ? ` · ${a.complement}` : ""}
                                                    {a?.reference ? ` · Ref.: ${a.reference}` : ""}
                                                </span>
                                            </span>
                                            {
                                                confirming ? (
                                                    <span className="flex shrink-0 items-center gap-2" onClick={e => e.stopPropagation()}>
                                                        <span className="text-xs text-laranjinha/70">Excluir?</span>
                                                        <button type="button" className="rounded bg-red-500 px-2 py-0.5 text-xs font-semibold text-white hover:bg-red-400" onClick={(e) => doDeleteUserAddress(e, a?._id)}>Sim</button>
                                                        <button type="button" className="rounded border border-primary/40 px-2 py-0.5 text-xs text-laranjinha hover:border-primary" onClick={handleCancelDelete}>Não</button>
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        aria-label={`Excluir endereço ${a?.name}`}
                                                        title="Excluir endereço"
                                                        className="shrink-0 px-1 text-laranjinha/40 hover:text-red-400"
                                                        onClick={(e) => handleConfirmDelete(e, a?._id)}
                                                    >
                                                        🗑
                                                    </button>
                                                )
                                            }
                                        </div>
                                    );
                                })
                            }
                        </div>
                    </>
                ) : delivery === "presencial" ? (
                    stores?.length ? (
                        <label className="flex items-center gap-2 text-sm text-laranjinha/80">
                            Loja de retirada:
                            <select
                                className="rounded border border-primary/40 bg-mgs-surface px-2 py-1 text-laranjinha"
                                value={store}
                                onChange={e => setStore(e.target.value)}
                            >
                                <option value={""}>Selecione…</option>
                                {
                                    stores.map((s, i) => (
                                        <option key={i} value={s?._id}>{s?.name}</option>
                                    ))
                                }
                            </select>
                        </label>
                    ) : (
                        <p className="text-sm text-laranjinha/60">
                            Não foi possível carregar as lojas. Recarregue a página para tentar de novo.
                        </p>
                    )
                ) : null
            }

            <div className="m-auto mt-5 flex w-fit gap-3">
                <Button click={goBack} classes="bg-purple-400 hover:bg-purple-300 px-4">← Voltar</Button>
                <Button click={handleNext} classes="bg-green-400 hover:bg-green-300 px-6" disabled={disableNext}>Avançar →</Button>
            </div>
            {/* LOADING OVERLAY */}
            {
                loading ?
                    <div className="absolute top-0 left-0 w-full h-full bg-[#000000dd] flex justify-center items-center">
                        <Loading />
                    </div>
                    : null
            }
        </div>
    )
}

export default DeliveryStep;
